import React, { useState, useEffect } from 'react';
import { FaUser } from 'react-icons/fa';
import { Collapse, Navbar, NavbarBrand, NavbarToggler, NavItem, NavLink, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { checkLoggedIn } from '../functions/CookieHelper';
import { LANGUAGE_SWITCHER_LIVE } from '../i18n';
import { applyAccountLanguage } from '../i18n/account';
import { CustomerSwitcher } from './controls/CustomerSwitcher';
import { LanguageMenu } from './controls/LanguageMenu';
import './NavMenu.css';

// XTrak is the mining brand, and mining customers are who use this app. The
// live oilfield views are the one outside audience, so they keep the DrillTrak
// wordmark; colours and font still follow the XTrak palette everywhere.
const OILFIELD_ROUTES = ['/remote', '/steering'];

const BrandLogo = ({ pathName }) => (
    OILFIELD_ROUTES.some(route => pathName === route || pathName.startsWith(route + '/'))
        ? <img src="/images/drilltrak_white_40.png" alt="DrillTrak" />
        // The supplied artwork, never re-set in a font: the wordmark face may not
        // be embedded. The white variant, because the colour one vanishes on red.
        : <img src="/images/xtrak-lockup-white-320.png" alt="XTrak Technology" height={40} />
);

export const NavMenu = () => {
    const { t } = useTranslation();
    const [isLoggedIn] = useState(checkLoggedIn());
    const [user, setUser] = useState({});
    const [collapsed, setCollapsed] = useState(true);
    const pathName = useLocation().pathname;

    useEffect(() => {
        setCollapsed(true);
    }, [pathName]);

    useEffect(() => {
        if (isLoggedIn) {
            const loadUser = async () => {
                const response = await fetch('api/identity/getuser');
                const data = await response.json();
                setUser(data);
                // The language saved on the account wins over whatever this browser used last.
                applyAccountLanguage(data);
                console.log('user', data);
            }
            loadUser();
        }
    }, [isLoggedIn]);

    const toggleNavbar = () => {
        setCollapsed(!collapsed);
    }

    let navContents = '';
    let userName = '';
    if (user) {
        userName = '[' + user.customer + ']';
    }

    const isSuperAdmin = !!(user && user.roles && user.roles.some(r => r.toLowerCase() === 'superadmin'));

    let userTag = (
        isLoggedIn &&
        <UncontrolledDropdown nav inNavbar>
              <DropdownToggle nav caret>
                <FaUser />{userName}
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem><NavLink tag={Link} className="text-dark" to="/users">{t('nav.account')}</NavLink></DropdownItem>
                {user && user.roles && user.roles.some(r => r.toLowerCase() === 'admin') && (
                    <>
                        <DropdownItem><NavLink tag={Link} className="text-dark" to="/admin/users">{t('nav.manageUsers')}</NavLink></DropdownItem>
                        <DropdownItem><NavLink tag={Link} className="text-dark" to="/admin/settings">{t('nav.settings')}</NavLink></DropdownItem>
                    </>
                )}
                {user && user.roles && user.roles.some(r => r.toLowerCase() === 'superadmin') && (
                    <DropdownItem><NavLink tag={Link} className="text-dark" to="/admin/tool-runs">Tool runs</NavLink></DropdownItem>
                )}
                <DropdownItem divider />
                <DropdownItem><NavLink tag={Link} className="text-dark" to="/logout">{t('nav.logout')}</NavLink></DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
    );

    let customerSwitcher = (
        isLoggedIn && isSuperAdmin &&
        <CustomerSwitcher />
    );

    // Super admins only until every customer-facing page is translated; see LANGUAGE_SWITCHER_LIVE.
    let languageMenu = (
        (LANGUAGE_SWITCHER_LIVE || isSuperAdmin) &&
        <LanguageMenu signedIn />
    );

    // The pages behind these links stay public — anyone given a direct URL can still
    // reach them without logging in. The menu itself is only rendered for signed-in
    // users, so the home page doesn't advertise them to every passer-by.
    let downloadsMenu = (
        <UncontrolledDropdown nav inNavbar>
            <DropdownToggle nav caret className="text-light">{t('nav.downloads')}</DropdownToggle>
            <DropdownMenu end>
                <DropdownItem><NavLink tag={Link} className="text-dark" to="/android">{t('nav.androidApp')}</NavLink></DropdownItem>
                <DropdownItem><NavLink tag={Link} className="text-dark" to="/navigator">Navigator</NavLink></DropdownItem>
                <DropdownItem divider />
                <DropdownItem><NavLink tag={Link} className="text-dark" to="/manuals">{t('nav.manuals')}</NavLink></DropdownItem>
            </DropdownMenu>
        </UncontrolledDropdown>
    );

    if (!isLoggedIn) {
        return (
            <header>
                <Navbar className="navbar-expand-sm navbar-toggleable-sm ng-white border-bottom box-shadow mb-3" container-fluid="true" dark>
                    <NavbarBrand tag={Link} to="/"><BrandLogo pathName={pathName} /></NavbarBrand>
                    <NavbarToggler onClick={toggleNavbar} className="mr-2" />
                    <Collapse className="d-sm-inline-flex flex-sm-row-reverse" isOpen={!collapsed} navbar>
                        <ul className="navbar-nav flex-grow">
                            {LANGUAGE_SWITCHER_LIVE && <LanguageMenu signedIn={false} />}
                            <NavItem>
                                <NavLink tag={Link} className="text-light" to="/login">{t('nav.login')}</NavLink>
                            </NavItem>
                            <NavItem>
                                <div style={{ width: 20 }} />
                            </NavItem>
                        </ul>
                    </Collapse>
                </Navbar>
            </header>
        );
    }

    switch (pathName) {
        case '/overview':
        case '/approval':
        case '/bulk':
        case '/manuals':
        case '/users':
        case '/activity':
            navContents = (<ul className="navbar-nav flex-grow">
                {/*<NavItem>*/}
                {/*    <NavLink tag={Link} className="text-light" to="/">Home</NavLink>*/}
                {/*</NavItem>*/}
                <NavItem>
                    <NavLink tag={Link} className="text-light" to="/overview">{t('nav.projects')}</NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag={Link} className="text-light" to="/approval">{t('nav.approval')}</NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag={Link} className="text-light" to="/bulk">{t('nav.bulkReview')}</NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag={Link} className="text-light" to="/activity">{t('nav.activity')}</NavLink>
                </NavItem>
                {downloadsMenu}
                {customerSwitcher}
                {languageMenu}
                {userTag}
                <NavItem>
                    <div style={{ width: 20 }} />
                </NavItem>
            </ul>);
            break;
        //case '/users':
        //    navContents = (<ul className="navbar-nav flex-grow">
        //        {/*<NavItem>*/}
        //        {/*    <NavLink tag={Link} className="text-light" to="/">Home</NavLink>*/}
        //        {/*</NavItem>*/}
        //        <NavItem>
        //            <NavLink tag={Link} className="text-light" to="/overview">Projects</NavLink>
        //        </NavItem>
        //        {userTag}
        //        <NavItem>
        //            <div style={{ width: 20 }} />
        //        </NavItem>
        //    </ul>);
        //    break;
        case '/hammertrak':
        case '/hammertrak/portal':
            navContents = (<ul className="navbar-nav flex-grow">
                <NavItem>
                    <NavLink tag={Link} className="text-light" to="/">{t('nav.home')}</NavLink>
                </NavItem>
                {downloadsMenu}
                {customerSwitcher}
                {languageMenu}
                {userTag}
                <NavItem>
                    <div style={{ width: 20 }} />
                </NavItem>
            </ul>);
            break;
        default:
            navContents = (<ul className="navbar-nav flex-grow">
                <NavItem>
                    <NavLink tag={Link} className="text-light" to="/overview">{t('nav.projects')}</NavLink>
                </NavItem>
                {user && user.customer === 'Capital' && (
                    <NavItem>
                        <NavLink tag={Link} className="text-light" to="/hammertrak">HammerTrak</NavLink>
                    </NavItem>
                )}
                {downloadsMenu}
                {customerSwitcher}
                {languageMenu}
                {userTag}
                <NavItem>
                    <div style={{ width: 20 }} />
                </NavItem>
            </ul>);
            break;
    }




    return (
        <header>
            <Navbar className="navbar-expand-sm navbar-toggleable-sm ng-white border-bottom box-shadow mb-3" container-fluid="true" dark>
                <NavbarBrand tag={Link} to="/"><BrandLogo pathName={pathName} /></NavbarBrand>
                <NavbarToggler onClick={toggleNavbar} className="mr-2" />
                <Collapse className="d-sm-inline-flex flex-sm-row-reverse" isOpen={!collapsed} navbar>
                    {isLoggedIn && navContents}
                </Collapse>
            </Navbar>
        </header>
    );

}
